import { Link } from "@tanstack/react-router";
import { ClipboardCheck, Users, PoundSterling, AlertTriangle, Phone, MessageCircle, Calendar, ArrowRight } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { trackEvent } from "@/lib/tracking";

/**
 * Homepage "MOTs, PCO Drivers & Value-for-Money Garage Support" strip.
 * Compact 4-card grid + CTA row. Targets MOT, PCO, value-for-money and
 * urgent warning-light intent without duplicating the main service grid.
 */
export function DriverSupportStrip() {
  const biz = getBusinessConfig();
  const cards = [
    {
      to: "/mot",
      icon: <ClipboardCheck className="h-5 w-5" />,
      title: "MOT Testing",
      desc: "Book MOT testing in E14 with clear advice before any repair work.",
    },
    {
      to: "/pco-mot-east-london",
      icon: <Users className="h-5 w-5" />,
      title: "PCO / Private Hire Drivers",
      desc: "Garage support for high-mileage private-hire and work vehicles.",
    },
    {
      to: "/value-for-money-garage-east-london",
      icon: <PoundSterling className="h-5 w-5" />,
      title: "Value-for-Money Repairs",
      desc: "Practical repair advice, fair pricing and no unnecessary work.",
    },
    {
      to: "/diagnostics",
      icon: <AlertTriangle className="h-5 w-5" />,
      title: "Urgent Warning Lights",
      desc: "Engine lights, ABS lights or dashboard warnings? Book diagnostics.",
    },
  ] as const;

  return (
    <section className="py-12 md:py-14">
      <div className="mx-auto max-w-6xl px-4">
        <div className="max-w-2xl">
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">
            Driver support
          </p>
          <h2 className="mt-2 text-2xl md:text-3xl font-bold text-foreground tracking-tight">
            MOTs, PCO Drivers & Value-for-Money Garage Support
          </h2>
          <p className="mt-3 text-sm md:text-base text-muted-foreground">
            Four quick routes into the help most East London drivers ask for — MOT, PCO/private hire, fair-priced repairs and urgent warning lights.
          </p>
        </div>
        <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              onClick={() => trackEvent("driver_support_strip_click", { target: c.to })}
              className="group rounded-2xl border border-border bg-card p-5 hover:border-primary/40 transition-colors flex flex-col"
            >
              <div className="flex items-start justify-between">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  {c.icon}
                </span>
                <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
              </div>
              <h3 className="mt-4 text-base font-bold text-foreground leading-snug">{c.title}</h3>
              <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed flex-1">{c.desc}</p>
            </Link>
          ))}
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          <a
            href={biz.phoneHref}
            onClick={() => trackEvent("call_click", { location: "driver_support_strip" })}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:bg-primary/90"
          >
            <Phone className="h-4 w-4" /> Call {biz.phone}
          </a>
          <a
            href={biz.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("whatsapp_click", { location: "driver_support_strip" })}
            className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1ea952]"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
          <Link
            to="/book"
            onClick={() => trackEvent("booking_request_start", { location: "driver_support_strip" })}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-bold text-foreground hover:border-primary/40"
          >
            <Calendar className="h-4 w-4" /> Request booking
          </Link>
        </div>
      </div>
    </section>
  );
}
